import React, { useState } from 'react';
import { format } from 'date-fns';
import { JOB_STATUSES, JOB_STAGES, STATUS_COLORS } from '../utils/constants';
import './JobTable.css';

const SortIcon = ({ active, order }) => (
  <span className={`sort-icon ${active ? 'sort-active' : ''}`}>
    {active ? (order === 'asc' ? '↑' : '↓') : '↕'}
  </span>
);

const JobTable = ({ jobs, loading, filters, setFilters, sort, setSort, onRowClick, onAddJob }) => {
  const [searchDraft, setSearchDraft] = useState(filters.search);
  const searchTimeout = React.useRef(null);

  const handleSearch = (val) => {
    setSearchDraft(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setFilters(f => ({ ...f, search: val }));
    }, 300);
  };

  const handleSort = (field) => {
    setSort(s => ({
      by: field,
      order: s.by === field && s.order === 'desc' ? 'asc' : 'desc'
    }));
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try { return format(new Date(d), 'MMM dd, yyyy'); }
    catch { return '—'; }
  };

  const formatSalary = (job) => {
    if (!job.salaryMin && !job.salaryMax) return '—';
    const cur = job.salaryCurrency || 'PHP';
    const fmt = (n) => n >= 1000 ? `${(n / 1000).toFixed(0)}k` : n;
    if (job.salaryMin && job.salaryMax) {
      if (job.salaryMin === job.salaryMax) return `${cur} ${fmt(job.salaryMin)}`;
      return `${cur} ${fmt(job.salaryMin)}–${fmt(job.salaryMax)}`;
    }
    if (job.salaryMin) return `${cur} ${fmt(job.salaryMin)}+`;
    return `${cur} up to ${fmt(job.salaryMax)}`;
  };

  return (
    <div className="table-section">
      {/* Toolbar */}
      <div className="table-toolbar">
        <div className="toolbar-search">
          <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Search company or role..."
            value={searchDraft}
            onChange={e => handleSearch(e.target.value)}
          />
          {searchDraft && (
            <button className="search-clear" onClick={() => handleSearch('')}>✕</button>
          )}
        </div>

        <div className="toolbar-filters">
          <select
            className="filter-select"
            value={filters.status}
            onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
          >
            <option value="all">All Status</option>
            {JOB_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          <select
            className="filter-select"
            value={filters.stage}
            onChange={e => setFilters(f => ({ ...f, stage: e.target.value }))}
          >
            <option value="all">All Stages</option>
            {JOB_STAGES.filter(s => s).map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          {(filters.status !== 'all' || filters.stage !== 'all' || filters.search) && (
            <button
              className="filter-clear-btn"
              onClick={() => {
                setFilters({ status: 'all', stage: 'all', search: '' });
                setSearchDraft('');
              }}
            >
              Clear filters
            </button>
          )}
        </div>

        <div className="toolbar-count">
          {jobs.length} application{jobs.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Table */}
      <div className="table-wrapper">
        {loading ? (
          <div className="table-loading">
            <div className="loading-spinner" />
            <span>Loading applications...</span>
          </div>
        ) : jobs.length === 0 ? (
          <div className="table-empty">
            <div className="empty-icon"></div>
            <h3>No applications yet</h3>
            <p>{filters.status !== 'all' || filters.stage !== 'all' || filters.search
              ? 'No results match your filters. Try adjusting them.'
              : 'Start tracking your job search by adding your first application.'
            }</p>
            {!filters.search && filters.status === 'all' && filters.stage === 'all' && (
              <button className="empty-add-btn" onClick={onAddJob}>+ Add Your First Application</button>
            )}
          </div>
        ) : (
          <table className="job-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleSort('company')}>
                  Company <SortIcon active={sort.by === 'company'} order={sort.order} />
                </th>
                <th>Role</th>
                <th>Source</th>
                <th className="sortable" onClick={() => handleSort('applicationDate')}>
                  Applied <SortIcon active={sort.by === 'applicationDate'} order={sort.order} />
                </th>
                <th>Location / Setup</th>
                <th>Salary</th>
                <th className="sortable" onClick={() => handleSort('status')}>
                  Status <SortIcon active={sort.by === 'status'} order={sort.order} />
                </th>
                <th>Stage</th>
                <th className="sortable" onClick={() => handleSort('lastUpdateDate')}>
                  Last Update <SortIcon active={sort.by === 'lastUpdateDate'} order={sort.order} />
                </th>
                <th>Next Step</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(job => {
                const statusColor = STATUS_COLORS[job.status] || {};
                return (
                  <tr key={job._id} onClick={() => onRowClick(job)} className="job-row">
                    <td>
                      <div className="company-cell">
                        <div className="company-avatar">
                          {job.company[0]?.toUpperCase()}
                        </div>
                        <span className="company-name">{job.company}</span>
                      </div>
                    </td>
                    <td className="role-cell">{job.role}</td>
                    <td>
                      {job.source
                        ? <span className="source-badge">{job.source}</span>
                        : <span className="cell-muted">—</span>
                      }
                    </td>
                    <td className="date-cell">{formatDate(job.applicationDate)}</td>
                    <td>
                      <div className="location-cell">
                        {job.location && <span className="location-text">{job.location}</span>}
                        <span className="setup-badge">
                          {job.workSetup !== 'Not specified' ? job.workSetup : '—'}
                        </span>
                      </div>
                    </td>
                    <td className="salary-cell">{formatSalary(job)}</td>
                    <td>
                      <span
                        className="status-pill"
                        style={{
                          background: statusColor.bg,
                          color: statusColor.text,
                          border: `1px solid ${statusColor.border}`
                        }}
                      >
                        {job.status}
                      </span>
                    </td>
                    <td>
                      {job.stage
                        ? <span className="stage-tag">{job.stage}</span>
                        : <span className="cell-muted">—</span>
                      }
                    </td>
                    <td className="date-cell">{formatDate(job.lastUpdateDate)}</td>
                    <td className="nextstep-cell">
                      {job.nextStep || <span className="cell-muted">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default JobTable;
